import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type TransactionInfoProps = {
  transaction: vp.BillPayTransactionResponse;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

const BillPayInfo: FC = ({ transaction }: TransactionInfoProps) => {
  const { state } = useLocation<DataProps>();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.BillPayInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsHeader') || ''} />
      </Box>
      <Table
        name="transaction-details"
        variant="pair"
        columns={{
          detailKey: { header: t('transactionDetailsHeader') || '' },
          detailValue: { header: '' },
        }}
        rows={[
          {
            detailKey: t('transactionDetails.transactionID'),
            detailValue: transaction.TransactionID,
          },
          {
            detailKey: t('transactionDetails.transactionStatus'),
            detailValue: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                    defaultValue: transaction.TransactionStatus,
                  }),
                )}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            detailKey: t('transactionDetails.transactionDate'),
            detailValue: transaction.TransactionDateTime,
          },
          {
            detailKey: t('transactionDetails.lastModified'),
            detailValue: transaction.LastModified,
          },
          {
            detailKey: t('transactionDetails.type'),
            detailValue:
              tGlobal('Transaction.TransactionHistory.transactionTypes.Bill Pay', {
                defaultValue: 'Bill Pay',
              }) || '',
          },
          {
            detailKey: t('transactionDetails.amount'),
            detailValue: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            detailKey: t('transactionDetails.currency'),
            detailValue: transaction.Currency,
          },
          {
            detailKey: t('transactionDetails.notes'),
            detailValue: transaction.Note || '',
          },
        ]}
      />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('payeeDetailsHeader') || ''} />
      </Box>
      <Table
        name="account-holder-details"
        variant="pair"
        columns={{
          display: { header: t('payeeDetailsHeader') || '' },
          value: { header: '' },
        }}
        rows={[
          {
            display: t('accountHolderDetails.payee'),
            value: `${transaction.MerchantName || transaction.Nickname}`,
            visible: true,
          },
          {
            display: t('accountHolderDetails.nickname'),
            value: `${transaction.Nickname}`,
            visible: transaction.Nickname && transaction.Nickname !== transaction.MerchantName,
          },
          {
            display: t('accountHolderDetails.paymentAccount'),
            value: `${transaction.PaymentAccount}`,
            visible: !!transaction.PaymentAccount,
          },
          {
            display: t('accountHolderDetails.category'),
            value: `${transaction.MerchantCategory}`,
            visible: false, //TODO: For next sprint make this is the correct value
          },
        ].filter(({ visible }) => visible)}
      />

      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoHeader') || ''} />
          </Box>
          <Table
            name="error-info"
            variant="pair"
            columns={{
              detailKey: { header: t('errorInfoHeader') || '' },
              detailValue: { header: '' },
            }}
            rows={[
              {
                detailKey: t('errorInfo.failureDateTime'),
                detailValue: transaction.LastModified,
              },
              {
                detailKey: t('errorInfo.amount'),
                detailValue: `$${formattedCurrency(transaction.Amount || '')}`,
              },
              {
                detailKey: t('errorInfo.failureReason'),
                detailValue: state.failureReason,
              },
            ]}
          />
        </>
      )}
    </>
  );
};

export default BillPayInfo;
